import { Annotation, AnnotationType, Point } from 'types/building'
import { Direction, Stairway } from 'types/krpano'

const Krpano = class Krpano {
  static AddDirectionArrow = (krpano: any, directions: Direction[]) => {
    directions.forEach((direction) => {
      krpano.call(
        `add_direction(${direction.id},${direction.h},${direction.v},${direction.target},${direction.point_to})`
      )
    })
  }

  static AddAnnotation = (
    krpano: any,
    annotations: Annotation[],
    isDisplay?: string | null
  ) => {
    annotations.forEach(async (annotation) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const {
        id,
        head_h: headH,
        head_v: headV,
        mid_h: midH,
        mid_v: midV,
        h,
        v,
        type,
        memo,
        set_annotation_name: setAnnotationName
      } = annotation
      const annotationType = Object.values(AnnotationType).includes(type || '')
        ? type
        : AnnotationType.TRADITION

      const displayText = setAnnotationName || ''
      const textContent = await this.FetchMemoContent(memo || '')
      krpano.call(
        `add_annotation(${id},${headH},${headV},${midH},${midV},${h},${v},${annotationType},<div class="ql-editor p-0">${textContent}</div>,${
          annotation.set_annotation_id
        }, ${isDisplay || 'true'},${displayText});`
      )
    })
  }

  static UnmountPanoFull = () => {
    const krpano = document.getElementById('embedpano-full') as any
    if (krpano) {
      ;(window as any).removepano('embedpano-full')
    }
  }

  static UnmountPanoPreview = () => {
    const krpano = document.getElementById('embedpano-preview') as any
    if (krpano) {
      ;(window as any).removepano('embedpano-preview')
    }
  }

  static UnmountPanoViewer = () => {
    const krpano = document.getElementById('embedpano-viewer') as any
    if (krpano) {
      ;(window as any).removepano('embedpano-viewer')
    }
  }

  static RemoveTempAnnotation = () => {
    const krpano = document.getElementById('embedpano-full') as any
    krpano?.call('hide_temp_hotspot();')
  }

  static UnmountFull = () => {
    this.UnmountPanoFull()
    this.UnmountPanoPreview()
  }

  static GetAngle = (dx: any, dy: any) => {
    const d = (Math.atan2(dy, dx) * 180) / Math.PI
    if (d < 0) {
      return d + 360
    }
    return d
  }

  static reConfigAngleOver360 = (angle: number) => {
    if (angle > 360) {
      return angle - 360
    }
    return angle
  }

  static TogglePointLock = (isLocked?: boolean) => {
    const krpano = document.getElementById('embedpano-full') as any
    krpano?.call(`set(islocked,${isLocked || false});`)
  }

  static LoadChecking = (callback: () => void) => {
    const fullpanoLoading = (
      document.getElementById('embedpano-full') as any
    )?.get('loadcomplete')

    const previewpanoLoading = (
      document.getElementById('embedpano-preview') as any
    )?.get('loadcomplete')
    if (
      (fullpanoLoading === undefined && previewpanoLoading === undefined) ||
      (fullpanoLoading === 'true' && previewpanoLoading === 'true')
    ) {
      callback()
    }
  }

  static LoadCheckingViewerMode = (callback: () => void) => {
    const fullpanoLoading = (
      document.getElementById('embedpano-viewer') as any
    )?.get('loadcomplete')

    if (fullpanoLoading || fullpanoLoading === undefined) {
      callback()
    }
  }

  static NormalizeHGap = (hGap: number): number => {
    if (hGap >= 360) {
      return this.NormalizeHGap(hGap - 360)
    }
    if (hGap < 0) {
      return this.NormalizeHGap(hGap + 360)
    }
    return hGap
  }

  static FormatStairName = (name?: string) => {
    const chars: any = {
      "'": '&apos;',
      '"': '&quot;'
    }
    return (
      name
        ? `<p>フロア：${
            name.length > 10 ? `${name?.slice(0, 10)}...` : name
          }</p>`
        : ''
    ).replace(/['"]/g, (m) => chars[m])
  }

  static AddStairway = (krpano: any, stairways: Stairway[]) => {
    if (krpano) {
      stairways.forEach((item) => {
        const newTooltip = this.FormatStairName(item.nextFloorName)

        krpano.call(
          `add_stairway(${item.id}, ${item.h}, ${item.v}, ${item.nextFloorId}, ${item.nextPointId}, ${newTooltip}, ${item.areaId})`
        )
      })
    }
  }

  static RevalidateDirection = (directions: Direction[]) => {
    const krpano = document.getElementById('embedpano-full') as any

    // const addingHgap = hgap > 180 ? 360 - hgap : hgap
    directions.forEach((item) => {
      // if (!item.dHgap) {
      krpano.call(`set(hotspot[arrow-${item.id}].ath, ${item.h});`)
      krpano.call(`set(hotspot[arrow-tooltip-${item.id}].ath, ${item.h});`)
      // }
    })
  }

  static FetchMemoContent = async (s3Link: string) => {
    // is valid digitori360 s3 link
    if (s3Link.startsWith('https://digitori360')) {
      const response = await fetch(s3Link)
      const blob = await response.blob()
      const textContent = await blob.text()
      return textContent
    }
    const entities: any = {
      '/': '&#x2F;',
      '\n': '<br/>',
      ',': '&#x2C;',
      '<': '&lt;',
      '>': '&gt;'
    }
    const textContent = s3Link
      .split('')
      .map((item: any) => (entities[item] ? entities[item] : item))
      .join('')
    return textContent
  }

  static IsMemoEmpty(value: string) {
    if (
      value.replace(/<(.|\n)*?>/g, '').trim().length === 0 &&
      !value.includes('<img')
    ) {
      return true
    }
    return false
  }

  static GetMemoKeyFromPresignedUrl(presignedUrl: string) {
    const startInd = presignedUrl.indexOf('/assets')
    const endIdx = presignedUrl.indexOf('.txt')
    const key = presignedUrl.substring(startInd, endIdx + 4)
    return key
  }

  static ToggleDisplayHotspot(krpano: any, isDisplay: boolean) {
    krpano?.call(`show_all_hotspot(${isDisplay})`)
  }

  static escapeXml(unsafe: string): string {
    if (!unsafe) return ''
    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;')
  }

  static generateSceneXML({
    sceneName,
    tilesUrl,
    multires,
    s3Path,
    thumbnailUrl
  }: {
    sceneName?: string
    tilesUrl?: string
    multires?: string
    s3Path: string
    thumbnailUrl: string
  }) {
    const xmlString =
      multires && multires !== 'CUBE_IMAGE'
        ? `
          <krpano>
              <scene name="${sceneName}">
                  <view
                      hlookat="0"
                      vlookat="0"
                      distortion="0.0"
                      fov="100"
                      limitview="auto"
                      fovmin="50"
                      fovmax="130"
                      fisheye="0.0"
                      stereographic="false"
                      cubestrip="true"
                      smooth="true"
                  />
                  <preview url="${this.escapeXml(tilesUrl || '')}preview.jpg" />
                  <image>
                      <cube url="${tilesUrl}%s/l%l/%v/l%l_%s_%v_%h.jpg" multires="${multires}" />
                  </image>
              </scene>
          </krpano>`.trim()
        : `
         <krpano>
                <scene name="${sceneName}">
                  <view hlookat="0" vlookat="0" distortion="0.0" fov="100" limitview="auto" fovmin="50" fovmax="130" />
                  <preview url="${this.escapeXml(thumbnailUrl)}" />
                  <image>
                    <sphere url="${this.escapeXml(s3Path)}" />
                  </image>
                </scene>
            </krpano>
            `.trim()

    return xmlString
  }

  static generateSceneXMLs(
    data: {
      id: number
      tilesUrl?: string
      multires?: string
      s3Path: string
      thumbnailUrl: string
    }[]
  ) {
    let sceneXml = '<krpano>'
    data.forEach((item) => {
      const { id, tilesUrl, multires, s3Path, thumbnailUrl } = item
      sceneXml +=
        multires && multires !== 'CUBE_IMAGE'
          ? `
          <scene name="scene_${id}">
              <view
                  hlookat="0"
                  vlookat="0"
                  distortion="0.0"
                  fov="100"
                  fovmin="50"
                  fovmax="130"
                  fisheye="0.0"
                  stereographic="false"
                  cubestrip="true"
                  smooth="true"
                  limitview="auto"
              />

              <preview url="${this.escapeXml(tilesUrl || '')}preview.jpg" />

              <image>
                  <cube url="${tilesUrl}%s/l%l/%v/l%l_%s_%v_%h.jpg" multires="${multires}" />
              </image>
          </scene>`
          : `<scene name="scene_${id}">
              <view hlookat="0" vlookat="0" distortion="0.0" fov="100" fovmin="50" fovmax="130" limitview="auto"/>
              <preview  url="${this.escapeXml(thumbnailUrl)}" />
              <image>
                <sphere url="${this.escapeXml(s3Path)}" />
              </image>
            </scene>
        `
    })
    sceneXml += '</krpano>'.trim()
    return sceneXml
  }

  static getScenceNameByFileId = (
    currentSelectedPoint: string | undefined,
    points: Point[],
    selectedSubImage: number | undefined
  ) => {
    if (!currentSelectedPoint) return 'scene_0'

    if (selectedSubImage) {
      return `scene_${selectedSubImage}`
    }

    const point = points?.find((point) => point.id === currentSelectedPoint)
    return point ? `scene_${point?.image360?.id}` : 'scene_0'
  }

  static loadViewDirection = (
    krpano: any,
    h: number,
    v: number,
    z: number,
    currentsence: string
  ) => {
    krpano.call(`load_view_direction(${h}, ${v}, ${z},${currentsence})`)
  }
}

export default Krpano
